import axios from "axios";

import {
  clearResolvedBackendOrigin,
  getBaseURLBackend,
  getInitialBackendOrigin,
  getInitialBaseURLBackend,
  getInitialFrontendOrigin,
} from "api/DomainResolver";

declare module "axios" {
  export interface AxiosRequestConfig {
    useInitialBackend?: boolean;
    skipErrorDomainCheck?: boolean;
    _retry?: boolean;
  }
}

const ACCESS_TOKEN_KEY = "pathways_access_token";
const REFRESH_TOKEN_KEY = "pathways_refresh_token";

export function setAuthTokens(tokens: { access?: string; refresh?: string }) {
  if (typeof localStorage === "undefined") {
    return;
  }

  if (tokens.access) {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access);
  }

  if (tokens.refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh);
  }
}

export function getAccessToken() {
  if (typeof localStorage === "undefined") {
    return null;
  }

  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken() {
  if (typeof localStorage === "undefined") {
    return null;
  }

  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function clearAuthTokens() {
  if (typeof localStorage === "undefined") {
    return;
  }

  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
}

export const backendOrigin = getInitialBackendOrigin();
export const baseURLBackend = getInitialBaseURLBackend();
export const baseURLFrontend = getInitialFrontendOrigin();

export const apiClient = axios.create({
  baseURL: baseURLBackend,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

const PUBLIC_ENDPOINTS = [
  "accounts/login/",
  "accounts/register/",
  "accounts/complete-register/",
  "accounts/token/refresh/",
  "accounts/logout/",
];

function redirectToLogin() {
  clearResolvedBackendOrigin();
  clearAuthTokens();

  if (typeof window !== "undefined") {
    window.location.href = "/login/";
  }
}

function normaliseUrl(url?: string) {
  return url ? url.replace(/^\/+/, "") : "";
}

function isPublicEndpoint(url?: string) {
  const cleanUrl = normaliseUrl(url);
  return PUBLIC_ENDPOINTS.some((publicUrl) => cleanUrl.includes(publicUrl));
}

function shouldTryDomainRecovery(err: any) {
  const original = err?.config;

  if (!original || original.skipErrorDomainCheck) {
    return false;
  }

  if (isPublicEndpoint(original.url)) {
    return false;
  }

  const isNetworkOrCorsError = !err.response;

  const isPermissionOrMissingError =
    err.response?.status === 403 || err.response?.status === 404;

  return isNetworkOrCorsError || isPermissionOrMissingError;
}

apiClient.interceptors.request.use((config) => {
  const publicEndpoint = isPublicEndpoint(config.url);

  config.headers = config.headers ?? {};

  if (publicEndpoint || config.useInitialBackend) {
    config.baseURL = getInitialBaseURLBackend();
  } else {
    config.baseURL = getBaseURLBackend();
  }

  const accessToken = getAccessToken();

  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }

  return config;
});

let isRefreshing = false;
let failedQueue: any[] = [];

function processQueue(error: any, value: boolean | null = null) {
  failedQueue.forEach((p) => {
    if (error) {
      p.reject(error);
    } else {
      p.resolve(value);
    }
  });


  failedQueue = [];
}

apiClient.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    const authEndpoint = isPublicEndpoint(original?.url);

    if (authEndpoint) {
      if (err.response?.status === 401) {
        redirectToLogin();
      }


      return Promise.reject(err);
    }

    if (err.response?.status === 401 && !original?._retry) {
      original._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => {
          original.baseURL = getBaseURLBackend();
          return apiClient(original);
        });
      }

      isRefreshing = true;

      try {
        const refreshToken = getRefreshToken();

        if (!refreshToken) {
          throw new Error("No refresh token available.");
        }

        const refreshResponse = await apiClient.post(
          "/accounts/token/refresh/",
          {
            refresh: refreshToken,
          },
          {
            skipErrorDomainCheck: true,
          }
        );

        setAuthTokens({
          access: refreshResponse.data.access,
          refresh: refreshResponse.data.refresh,
        });

        original.baseURL = getBaseURLBackend();

        processQueue(null, true);
        return apiClient(original);
      } catch (refreshError: any) {
        processQueue(refreshError, null);
        redirectToLogin();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (shouldTryDomainRecovery(err)) {
      try {
        const { checkDomainRedirectFromOrganisation } = await import(
          "api/DomainRecovery"
        );

        const redirected = await checkDomainRedirectFromOrganisation();

        if (redirected) {
          return new Promise(() => {
            // Browser is redirecting.
          });
        }
      } catch (recoveryError) {
        console.warn("Domain recovery failed:", recoveryError);
      }
    }

    return Promise.reject(err);
  }
);